import React, { useState, useEffect, useRef } from 'react';
import { InputLabel, OutlinedInput, Tooltip, Typography, Button } from '@mui/material';
import axios from 'axios';
import "../../styles/UserDashboard/Kyc.scss";
import { Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';

// Utility function to upload a file as data URI to backend
const allowedExtensions = [
  "png", "jpg", "jpeg", "pdf", "docx", "doc", "xlsx", "csv", "openxmlformats", "octet-stream"
];


function getMediaType(file) {
  const ext = file.name.split('.').pop().toLowerCase();
  const allowed = ["png", "jpg", "jpeg", "pdf", "docx", "doc", "xlsx", "csv"];
  if (allowed.includes(ext)) return ext;
  return "bin";
}

// Add file size validation - Changed to 5MB
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB






const FilePreviewGallery = ({ files, onRemove, type = 'image' }) => {
  if (!Array.isArray(files) || files.length === 0) return null;

  return (
    <div style={{
      marginTop: '16px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
      gap: '12px'
    }}>
      {files.map((file, idx) => {
        const hasPreview = file.preview && file.preview !== null && file.preview.length > 0;
        const fileName = file.name || 'unknown file';
        const isPDF = file.type === 'application/pdf' || (fileName && fileName.toLowerCase().endsWith('.pdf'));
        const isImage = file.type?.startsWith('image/') && hasPreview;
        const fileExtension = fileName.split('.').pop() || 'file';

        return (
          <div
            key={`${fileName}-${idx}-${Date.now()}`}
            style={{
              position: 'relative',
              borderRadius: '8px',
              overflow: 'hidden',
              backgroundColor: '#f0f0f0',
              aspectRatio: '1',
              border: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            className="file-preview-item"
          >
            {(isImage || (isPDF && hasPreview)) ? (
              <img
                src={file.preview}
                alt={fileName}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : null}

            {/* Fallback for non-images or failed previews */}
            <div style={{
              display: (isImage || (isPDF && hasPreview)) ? 'none' : 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              backgroundColor: isPDF ? '#ffebee' : '#f5f5f5',
              padding: '8px'
            }}>
              {isPDF ? (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#f44336" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <polyline points="14,2 14,8 20,8" stroke="#f44336" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <text x="12" y="18" textAnchor="middle" fontSize="8" fill="#f44336" fontWeight="bold">PDF</text>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M13 2v7h7" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              <span style={{
                fontSize: '9px',
                marginTop: '4px',
                textAlign: 'center',
                color: '#999',
                wordBreak: 'break-all',
                maxWidth: '100%',
                padding: '0 4px'
              }}>
                {fileExtension.toUpperCase()}
              </span>
            </div>

            {/* Hover overlay with delete button */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.2s'
              }}
              className="preview-overlay"
            >
              <button
                onClick={() => onRemove(idx)}
                style={{
                  background: '#D32F2F',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="5" y1="5" x2="13" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                  <line x1="13" y1="5" x2="5" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <style>{`
              .file-preview-item:hover .preview-overlay {
                display: flex !important;
                opacity: 1 !important;
              }
            `}</style>

            {/* Tooltip with filename */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '4px',
              fontSize: '9px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {fileName}
            </div>
          </div>
        );
      })}
    </div>
  );
};



const KYCBusinessMonitoringForm = ({ onComplete, userId, token }) => {
  const [facilityInfo, setFacilityInfo] = useState({
    facilityName: '',
    facilityDescription: '',
    facilityLocation: '',
    projectStartDate: '',
    projectEndDate: '',
    solarElectricityUsage: null,
    generatorFuelType: '',
    generatorFuelTypeOther: '',
    utilityBill: [],
    usesGridElectricity: null,
  });
  const [solarElectricityUsage, setSolarElectricityUsage] = useState(null);
  const [usesGridElectricity, setUsesGridElectricity] = useState(null);
  const [showDropdownGenerator, setShowDropdownGenerator] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [loading, setLoading] = useState(false);
  const fileStorageRef = React.useRef(new Map());

  async function uploadFileToBackend(fileObj, userId, token) {
    try {
      // Get the actual File object from storage using the ID
      const file = fileStorageRef.current.get(fileObj.id);

      if (!file) {
        throw new Error(`File not found in storage. ID: ${fileObj.id}`);
      }

      if (!(file instanceof File || file instanceof Blob)) {
        throw new Error(`Invalid file type: ${file?.constructor?.name}. Expected File or Blob`);
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`File size too large. Maximum allowed: 5MB`);
      }

      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        throw new Error("File type not allowed. Allowed: " + allowedExtensions.join(", "));
      }

      console.log('Starting to read file:', file.name);

      // Convert file to Base64
      const dataUri = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          console.log('File read successfully');
          resolve(reader.result);
        };
        reader.onerror = (error) => {
          console.error('FileReader error:', error);
          reject(new Error("Failed to read file: " + error.message));
        };
      });

      const media_type = getMediaType(file);

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        throw new Error("API URL not configured. Check REACT_APP_API_URL environment variable");
      }

      if (!token) {
        throw new Error("Authentication token is missing");
      }

      if (!userId) {
        throw new Error("User ID is missing");
      }

      console.log('Uploading file:', {
        fileName: file.name,
        fileSize: file.size,
        mediaType: media_type
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/open/v1/upload`,
        {
          media_type,
          folder: "documents",
          user_id: userId,
          contents: [dataUri],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      console.log('Upload response:', response.data);

      if (response?.data?.body?.success) {
        return response.data.body.data[0];
      } else if (response?.data?.success) {
        return response.data.data[0];
      } else {
        throw new Error("File upload failed: " + (response?.data?.body?.message || response?.data?.message || "Unknown error"));
      }
    } catch (error) {
      console.error('Upload error details:', error);

      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        throw new Error(`Upload failed (${error.response.status}): ${error.response.data?.message || error.message}`);
      } else if (error.request) {
        throw new Error("No response from server. Check your network connection and API endpoint.");
      } else {
        throw error;
      }
    }
  }

  // Helper function to create file preview - FIXED FOR PDFs
  const createFilePreview = (file) => {
    return new Promise((resolve) => {
      const isImage = file.type?.startsWith('image/');
      const isPDF = file.type === 'application/pdf' || (file.name && file.name.toLowerCase().endsWith('.pdf'));

      // Ensure file has required properties
      const fileWithDefaults = {
        ...file,
        name: file.name || 'Unknown File',
        type: file.type || 'application/octet-stream'
      };

      // For non-image files (PDFs, etc.), resolve immediately without preview
      if (!isImage) {
        resolve({
          ...fileWithDefaults,
          preview: null,
          isImage: false,
          isPDF: isPDF
        });
        return;
      }

      // Only create preview for images
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve({
          ...fileWithDefaults,
          preview: e.target.result,
          isImage: true,
          isPDF: false
        });
      };
      reader.onerror = () => {
        resolve({
          ...fileWithDefaults,
          preview: null,
          isImage: false,
          isPDF: isPDF
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = async (e) => {
    setUploadError('');
    const files = Array.from(e.target.files);

    // Check file sizes before processing
    const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setUploadError(`File size too large. Maximum allowed: 5MB`);
      return;
    }

    // Create file display objects with unique IDs
    const filesWithPreviews = [];

    for (const file of files) {
      const isImage = file.type?.startsWith('image/');
      const isPDF = file.type === 'application/pdf' ||
        (file.name && file.name.toLowerCase().endsWith('.pdf'));

      let preview = null;

      // Only create preview for images
      if (isImage) {
        try {
          preview = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
          });
        } catch (err) {
          console.error('Error creating preview:', err);
          preview = null;
        }
      }

      // Create unique ID for this file
      const fileId = `file-${Date.now()}-${Math.random()}`;

      // Store the actual File object in the ref Map
      fileStorageRef.current.set(fileId, file);

      filesWithPreviews.push({
        id: fileId,
        name: file.name,
        type: file.type,
        size: file.size,
        preview: preview,
        isImage: isImage,
        isPDF: isPDF
      });
    }

    setFacilityInfo(prev => ({
      ...prev,
      utilityBill: [...(prev.utilityBill || []), ...filesWithPreviews]
    }));
  };

  const handleRemoveUtilityBill = (index) => {
    setUploadError('');
    setFacilityInfo(prev => {
      const fileToRemove = prev.utilityBill[index];
      // Clean up the stored file from ref
      if (fileToRemove?.id) {
        fileStorageRef.current.delete(fileToRemove.id);
      }
      return {
        ...prev,
        utilityBill: prev.utilityBill.filter((_, i) => i !== index)
      };
    });
  };

  const handleFacilityChange = (field, value) => {
    setFacilityInfo(prev => ({ ...prev, [field]: value }));
  };

  const validateFacilityForm = () => {
    const requiredFields = ['facilityName', 'facilityDescription', 'facilityLocation', 'projectStartDate', 'projectEndDate', 'generatorFuelType', 'utilityBill'];
    return requiredFields.every(field => {
      if (Array.isArray(facilityInfo[field])) {
        return facilityInfo[field].length > 0;
      }
      return facilityInfo[field] && typeof facilityInfo[field] === 'string' && facilityInfo[field].trim() !== '';
    });
  };

  const handleFacilityComplete = async (e) => {
    e.preventDefault();

    // Validate form fields first
    if (!validateFacilityForm()) {
      alert('Please fill in all required fields before continuing.');
      return;
    }

    // Validate project dates
    if (Date.now() > Date.parse(facilityInfo.projectEndDate)) {
      alert("Project end date can not be in the past");
      return;
    }

    if (Date.parse(facilityInfo.projectStartDate) > Date.parse(facilityInfo.projectEndDate)) {
      alert("Project start date can not be ahead of Project end date ");
      return;
    }

    try {
      // Upload all files to backend as data URI
      const uploadedUtilityUrls = await Promise.all(
        (facilityInfo.utilityBill || []).map(file => uploadFileToBackend(file, userId, token))
      );

      if (onComplete) onComplete({
        ...facilityInfo,
        utilityBill: uploadedUtilityUrls,
      });
    } catch (err) {
      alert("Failed to upload utility bill(s). Please try again.");
    }
    setLoading(false);
  };

  const facilityStepComplete = validateFacilityForm();

  return (
    <div className="kyc_modal_container">
      <div className="kyc_modal">
        <div className="title" style={{ textAlign: 'start', flex: 1 }}>Step into the future...</div>

        <div className="subtitle" style={{ textAlign: 'start' }}>
          Complete this KYC step, your safety our priority
        </div>

        <form className="kyc_grid" onSubmit={handleFacilityComplete}>
          <div className="kyc_grid">
            <div>
              <InputLabel
                className="kyc_label"
                htmlFor="organizationDescription"
              >
                Facility name
                <Tooltip
                  title={
                    <Typography variant="h6">
                      Enter  facility name
                    </Typography>
                  }
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="-0.5 0 25 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      {" "}
                      <path
                        d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                        stroke="#667185"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>{" "}
                      <path
                        d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                        fill="#667185"
                      ></path>{" "}
                    </g>
                  </svg>
                </Tooltip>
              </InputLabel>

              <OutlinedInput
                className="kyc_input"
                required
                id="organizationDescription"
                type="text"
                placeholder="Enter facility/project name"
                name="organizationDescription"
                value={facilityInfo.facilityName}
                onChange={(e) => handleFacilityChange('facilityName', e.target.value)}
              />
            </div>
            <div>
              <InputLabel
                className="kyc_label"
                htmlFor="organizationDescription"
              >
                Facility description
                <Tooltip
                  title={
                    <Typography variant="h6">
                      A brief explanation of what your facility does
                    </Typography>
                  }
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="-0.5 0 25 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      {" "}
                      <path
                        d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                        stroke="#667185"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>{" "}
                      <path
                        d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                        fill="#667185"
                      ></path>{" "}
                    </g>
                  </svg>
                </Tooltip>
              </InputLabel>

              <OutlinedInput
                className="kyc_input"
                required
                id="organizationDescription"
                type="text"
                placeholder="Provide a summary description of the facility to enable an understanding of the nature and its implementation."
                name="organizationDescription"
                value={facilityInfo.facilityDescription}
                onChange={(e) => handleFacilityChange('facilityDescription', e.target.value)}
              />
              <div className="kyc_instructions">
                <div className=" mt-4 kyc_instructions__title">
                  Include the following:
                </div>
                <div className="kyc_instructions__subtitle">
                  <svg
                    width="4"
                    height="4"
                    viewBox="0 0 4 4"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="2" cy="2" r="2" fill="#D9D9D9" />
                  </svg>
                  The objectives of the project
                </div>
                <div className="kyc_instructions__subtitle">
                  <svg
                    width="4"
                    height="4"
                    viewBox="0 0 4 4"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="2" cy="2" r="2" fill="#D9D9D9" />
                  </svg>
                  Describe the project activity or activities
                </div>
                <div className="kyc_instructions__subtitle">
                  <svg
                    width="4"
                    height="4"
                    viewBox="0 0 4 4"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="2" cy="2" r="2" fill="#D9D9D9" />
                  </svg>
                  An estimate of annual average, total reductions and removals
                </div>
              </div>
            </div>
            <div>
              <InputLabel className="kyc_label" htmlFor="location">
                Facility location
                <Tooltip
                  title={
                    <Typography variant="h6">
                      Enter facility address
                    </Typography>
                  }
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="-0.5 0 25 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      {" "}
                      <path
                        d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                        stroke="#667185"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>{" "}
                      <path
                        d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                        fill="#667185"
                      ></path>{" "}
                    </g>
                  </svg>
                </Tooltip>
              </InputLabel>

              <OutlinedInput
                className="kyc_input"
                required
                id="location"
                type="text"
                placeholder="Enter facility/project address"
                name="location"
                value={facilityInfo.facilityLocation}
                onChange={(e) => handleFacilityChange('facilityLocation', e.target.value)}
              />
            </div>
            <div className="kyc_group">
              <div>
                <InputLabel className="kyc_label" htmlFor="location">
                  Facility start date

                  <Tooltip
                    title={
                      <Typography variant="h6">
                        Enter  facility/project start
                      </Typography>
                    }
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="-0.5 0 25 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        {" "}
                        <path
                          d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                          stroke="#667185"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>{" "}
                        <path
                          d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                          fill="#667185"
                        ></path>{" "}
                      </g>
                    </svg>
                  </Tooltip>
                </InputLabel>

                <input
                  className="kyc_input kyc_input-date"
                  required
                  id="location"
                  type="date"
                  placeholder="Enter facility/project start date"
                  name="location"
                  value={facilityInfo.projectStartDate}
                  onChange={(e) => handleFacilityChange('projectStartDate', e.target.value)}
                />
              </div>
              <div>
                <InputLabel className="kyc_label" htmlFor="location">
                  Facility end date

                  <Tooltip
                    title={
                      <Typography variant="h6">
                        Enter facility/project end date
                      </Typography>
                    }
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="-0.5 0 25 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        {" "}
                        <path
                          d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                          stroke="#667185"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>{" "}
                        <path
                          d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                          fill="#667185"
                        ></path>{" "}
                      </g>
                    </svg>
                  </Tooltip>
                </InputLabel>

                <input
                  className="kyc_input kyc_input-date"
                  id="location"
                  type="date"
                  placeholder="Enter facility/project end date"
                  name="location"
                  value={facilityInfo.projectEndDate}
                  onChange={(e) => handleFacilityChange('projectEndDate', e.target.value)}
                />
              </div>
            </div>
            <InputLabel htmlFor="additonalInfo"
              style={{
                fontSize: '1.6rem',
                fontWeight: 600,
                color: ' #282828',
              }}
            >
              Main energy source
            </InputLabel>
            <div className="kyc-checkbox-grid" style={{ marginTop: 0, marginBottom: 0 }}>
              <div className="kyc-checkbox-grid__title" style={{ marginTop: 0, marginBottom: 0 }}>
                <InputLabel
                  htmlFor="quantifiedBasedYearEmission"
                  style={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#101828',
                  }}
                >
                  Solar energy
                  <Tooltip
                    title={
                      <Typography variant="h6">
                        Solar energy
                      </Typography>
                    }
                  >
                    <svg
                      style={{ marginLeft: 6, verticalAlign: 'middle', display: 'inline-block' }}
                      width="18"
                      height="18"
                      viewBox="-0.5 0 25 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"

                    >
                      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path
                          d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                          stroke="#667185"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                        <path
                          d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                          fill="#667185"
                        ></path>
                      </g>
                    </svg>
                  </Tooltip>
                </InputLabel>
              </div>
              <div className="kyc-checkbox-grid__check-box" style={{ marginTop: 0, marginBottom: 0 }}>
                <div>
                  {solarElectricityUsage !== "true" && (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => {
                        setSolarElectricityUsage("true");
                        handleFacilityChange('solarElectricityUsage', true);
                      }}
                    >
                      <rect
                        x="1"
                        y="1"
                        width="16"
                        height="16"
                        rx="4"
                        stroke="url(#paint0_linear_2889_18043)"
                        strokeWidth="1.33333"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_2889_18043"
                          x1="1"
                          y1="1.36719"
                          x2="20.0689"
                          y2="10.7886"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#006633" />
                          <stop offset="1" stopColor="#02A4CC" />
                        </linearGradient>
                      </defs>
                    </svg>
                  )}
                  {solarElectricityUsage === "true" && (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => {
                        setSolarElectricityUsage("");
                        handleFacilityChange('solarElectricityUsage', null);
                      }}
                    >
                      <rect
                        x="1"
                        y="1"
                        width="16"
                        height="16"
                        rx="4"
                        stroke="url(#paint0_linear_2889_18037)"
                        strokeWidth="1.33333"
                      />
                      <path
                        d="M5.5 9.49992L7.83333 11.8333L12.5 6.83325"
                        stroke="url(#paint1_linear_2889_18037)"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_2889_18037"
                          x1="1"
                          y1="1.36719"
                          x2="20.0689"
                          y2="10.7886"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#006633" />
                          <stop offset="1" stopColor="#02A4CC" />
                        </linearGradient>
                        <linearGradient
                          id="paint1_linear_2889_18037"
                          x1="5.5"
                          y1="6.948"
                          x2="12.5203"
                          y2="11.8039"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#006633" />
                          <stop offset="1" stopColor="#02A4CC" />
                        </linearGradient>
                      </defs>
                    </svg>
                  )}
                  Yes
                </div>
                <div>
                  {solarElectricityUsage !== "false" && (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => {
                        setSolarElectricityUsage("false");
                        handleFacilityChange('solarElectricityUsage', false);
                      }}
                    >
                      <rect
                        x="1"
                        y="1"
                        width="16"
                        height="16"
                        rx="4"
                        stroke="url(#paint0_linear_2889_18043)"
                        strokeWidth="1.33333"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_2889_18043"
                          x1="1"
                          y1="1.36719"
                          x2="20.0689"
                          y2="10.7886"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#006633" />
                          <stop offset="1" stopColor="#02A4CC" />
                        </linearGradient>
                      </defs>
                    </svg>
                  )}
                  {solarElectricityUsage === "false" && (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => {
                        setSolarElectricityUsage("");
                        handleFacilityChange('solarElectricityUsage', null);
                      }}
                    >
                      <rect
                        x="1"
                        y="1"
                        width="16"
                        height="16"
                        rx="4"
                        stroke="url(#paint0_linear_2889_18037)"
                        strokeWidth="1.33333"
                      />
                      <path
                        d="M5.5 9.49992L7.83333 11.8333L12.5 6.83325"
                        stroke="url(#paint1_linear_2889_18037)"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_2889_18037"
                          x1="1"
                          y1="1.36719"
                          x2="20.0689"
                          y2="10.7886"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#006633" />
                          <stop offset="1" stopColor="#02A4CC" />
                        </linearGradient>
                        <linearGradient
                          id="paint1_linear_2889_18037"
                          x1="5.5"
                          y1="6.948"
                          x2="12.5203"
                          y2="11.8039"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#006633" />
                          <stop offset="1" stopColor="#02A4CC" />
                        </linearGradient>
                      </defs>
                    </svg>
                  )}
                  No
                </div>
              </div>
            </div>

            <div className="kyc-checkbox-grid" style={{ marginTop: 0, marginBottom: 0 }}>
              <div className="kyc-checkbox-grid__title" style={{ marginTop: 0, marginBottom: 0 }}>
                <InputLabel
                  htmlFor="quantifiedBasedYearEmission"
                  style={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#101828',
                  }}
                >
                  Does your facility use grid electricity
                  <Tooltip
                    title={
                      <Typography variant="h6">
                        Does your facility use grid electricity
                      </Typography>
                    }
                  >
                    <svg
                      style={{ verticalAlign: 'middle', display: 'inline-block', marginLeft: 6 }}
                      width="18"
                      height="18"
                      viewBox="-0.5 0 25 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path
                          d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                          stroke="#667185"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                        <path
                          d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                          fill="#667185"
                        ></path>
                      </g>
                    </svg>
                  </Tooltip>
                </InputLabel>
              </div>
              <div className="kyc-checkbox-grid__check-box" style={{ marginTop: 0, marginBottom: 0 }}>
                <div>
                  {usesGridElectricity !== "true" && (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => {
                        setUsesGridElectricity("true");
                        handleFacilityChange('usesGridElectricity', true);
                      }}
                    >
                      <rect
                        x="1"
                        y="1"
                        width="16"
                        height="16"
                        rx="4"
                        stroke="url(#paint0_linear_2889_18043)"
                        strokeWidth="1.33333"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_2889_18043"
                          x1="1"
                          y1="1.36719"
                          x2="20.0689"
                          y2="10.7886"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#006633" />
                          <stop offset="1" stopColor="#02A4CC" />
                        </linearGradient>
                      </defs>
                    </svg>
                  )}
                  {usesGridElectricity === "true" && (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => {
                        setUsesGridElectricity("");
                        handleFacilityChange('usesGridElectricity', null);
                      }}
                    >
                      <rect
                        x="1"
                        y="1"
                        width="16"
                        height="16"
                        rx="4"
                        stroke="url(#paint0_linear_2889_18037)"
                        strokeWidth="1.33333"
                      />
                      <path
                        d="M5.5 9.49992L7.83333 11.8333L12.5 6.83325"
                        stroke="url(#paint1_linear_2889_18037)"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_2889_18037"
                          x1="1"
                          y1="1.36719"
                          x2="20.0689"
                          y2="10.7886"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#006633" />
                          <stop offset="1" stopColor="#02A4CC" />
                        </linearGradient>
                        <linearGradient
                          id="paint1_linear_2889_18037"
                          x1="5.5"
                          y1="6.948"
                          x2="12.5203"
                          y2="11.8039"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#006633" />
                          <stop offset="1" stopColor="#02A4CC" />
                        </linearGradient>
                      </defs>
                    </svg>
                  )}
                  Yes
                </div>
                <div>
                  {usesGridElectricity !== "false" && (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => {
                        setUsesGridElectricity("false");
                        handleFacilityChange('usesGridElectricity', false);
                      }}
                    >
                      <rect
                        x="1"
                        y="1"
                        width="16"
                        height="16"
                        rx="4"
                        stroke="url(#paint0_linear_2889_18043)"
                        strokeWidth="1.33333"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_2889_18043"
                          x1="1"
                          y1="1.36719"
                          x2="20.0689"
                          y2="10.7886"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#006633" />
                          <stop offset="1" stopColor="#02A4CC" />
                        </linearGradient>
                      </defs>
                    </svg>
                  )}
                  {usesGridElectricity === "false" && (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => {
                        setUsesGridElectricity("");
                        handleFacilityChange('usesGridElectricity', null);
                      }}
                    >
                      <rect
                        x="1"
                        y="1"
                        width="16"
                        height="16"
                        rx="4"
                        stroke="url(#paint0_linear_2889_18037)"
                        strokeWidth="1.33333"
                      />
                      <path
                        d="M5.5 9.49992L7.83333 11.8333L12.5 6.83325"
                        stroke="url(#paint1_linear_2889_18037)"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_2889_18037"
                          x1="1"
                          y1="1.36719"
                          x2="20.0689"
                          y2="10.7886"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#006633" />
                          <stop offset="1" stopColor="#02A4CC" />
                        </linearGradient>
                        <linearGradient
                          id="paint1_linear_2889_18037"
                          x1="5.5"
                          y1="6.948"
                          x2="12.5203"
                          y2="11.8039"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#006633" />
                          <stop offset="1" stopColor="#02A4CC" />
                        </linearGradient>
                      </defs>
                    </svg>
                  )}
                  No
                </div>
              </div>
            </div>
            <div>
              <InputLabel
                className="kyc_label"
                htmlFor="quantifiedBasedYearEmission"
              >
                Generator fuel type

                <Tooltip
                  title={
                    <Typography variant="h6">
                      Select fuel type used in facility
                    </Typography>
                  }
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="-0.5 0 25 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      {" "}
                      <path
                        d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                        stroke="#667185"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>{" "}
                      <path
                        d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                        fill="#667185"
                      ></path>{" "}
                    </g>
                  </svg>
                </Tooltip>
              </InputLabel>
              <div className="kyc_dropdown_checkbox_container">
                <div className="kyc_dropdown" style={{ position: 'relative' }}>
                  <div
                    className="kyc_input kyc_dropdown_input"
                    style={{ cursor: 'pointer', minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    onClick={() => setShowDropdownGenerator(prev => !prev)}
                  >
                    <span>
                      {facilityInfo.generatorFuelType
                        ? facilityInfo.generatorFuelType === 'others' && facilityInfo.generatorFuelTypeOther && facilityInfo.generatorFuelTypeOther.trim() !== ''
                          ? facilityInfo.generatorFuelTypeOther
                          : facilityInfo.generatorFuelType
                        : 'Select generator fuel type'}
                    </span>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginRight: 24, transition: 'transform 0.2s', transform: showDropdownGenerator ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    >
                      <path d="M6 8L10 12L14 8" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  {showDropdownGenerator && (
                    <div className="kyc_dropdown_menu" style={{ position: 'absolute', zIndex: 10, background: '#fff', border: '1px solid #ccc', borderRadius: 8, width: '100%', marginTop: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                      {[
                        { label: 'Coal', value: 'Coal' },
                        { label: 'Diesel', value: 'Diesel' },
                        { label: 'Natural gas', value: 'Natural gas' },
                        { label: 'Petroleum', value: 'Petroleum' },
                        { label: 'LPG', value: 'LPG' },
                        { label: 'Propane', value: 'Propane' },
                        { label: 'Kerosene', value: 'kerosene' },
                        { label: 'Shale oil', value: 'Shale' },
                        { label: 'Not Applicable', value: 'notApplicablem' },
                        { label: 'Others (please Specify)', value: 'others' },
                      ].map(option => (
                        <div key={option.value} style={{ display: 'flex', alignItems: 'center', padding: '6px 12px' }}>
                          {facilityInfo.generatorFuelType === option.value ? (
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 18 18"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              onClick={() => {
                                // Deselect current option
                                handleFacilityChange('generatorFuelType', '');
                                if (option.value === 'others') handleFacilityChange('generatorFuelTypeOther', '');
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <rect
                                x="1"
                                y="1"
                                width="16"
                                height="16"
                                rx="4"
                                stroke="url(#paint0_linear_2889_18037)"
                                strokeWidth="1.33333"
                              />
                              <path
                                d="M5.5 9.49992L7.83333 11.8333L12.5 6.83325"
                                stroke="url(#paint1_linear_2889_18037)"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <defs>
                                <linearGradient
                                  id="paint0_linear_2889_18037"
                                  x1="1"
                                  y1="1.36719"
                                  x2="20.0689"
                                  y2="10.7886"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop stopColor="#006633" />
                                  <stop offset="1" stopColor="#02A4CC" />
                                </linearGradient>
                                <linearGradient
                                  id="paint1_linear_2889_18037"
                                  x1="5.5"
                                  y1="6.948"
                                  x2="12.5203"
                                  y2="11.8039"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop stopColor="#006633" />
                                  <stop offset="1" stopColor="#02A4CC" />
                                </linearGradient>
                              </defs>
                            </svg>
                          ) : (
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 18 18"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              onClick={() => {
                                // Select this option and clear previous selection
                                handleFacilityChange('generatorFuelType', option.value);
                                // Clear the "others" text field if switching away from "others"
                                if (option.value !== 'others') handleFacilityChange('generatorFuelTypeOther', '');
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <rect
                                x="1"
                                y="1"
                                width="16"
                                height="16"
                                rx="4"
                                stroke="url(#paint0_linear_2889_18043)"
                                strokeWidth="1.33333"
                              />
                              <defs>
                                <linearGradient
                                  id="paint0_linear_2889_18043"
                                  x1="1"
                                  y1="1.36719"
                                  x2="20.0689"
                                  y2="10.7886"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop stopColor="#006633" />
                                  <stop offset="1" stopColor="#02A4CC" />
                                </linearGradient>
                              </defs>
                            </svg>
                          )}
                          <span style={{ marginLeft: 8, fontSize: 12 }}>{option.label}</span>
                          {option.value === 'others' && facilityInfo.generatorFuelType === 'others' && (
                            <OutlinedInput
                              className="kyc_input"
                              style={{ marginLeft: 12, width: 180, height: 32 }}
                              placeholder="Please specify"
                              value={facilityInfo.generatorFuelTypeOther || ''}
                              onChange={e => handleFacilityChange('generatorFuelTypeOther', e.target.value)}
                            />
                          )}
                        </div>
                      ))}
                      <div style={{ textAlign: 'right', padding: '8px 12px' }}>
                        <Button size="small" variant="outlined" onClick={() => setShowDropdownGenerator(false)}>Done</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Upload utility bill */}
            <div style={{ marginBottom: '24px' }}>
              <InputLabel
                className="kyc_label"
                htmlFor="quantifiedBasedYearEmission"
              >
                Upload utility bill
                <Tooltip>
                  <svg
                    width="18"
                    height="18"
                    viewBox="-0.5 0 25 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      {" "}
                      <path
                        d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                        stroke="#667185"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>{" "}
                      <path
                        d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                        fill="#667185"
                      ></path>{" "}
                    </g>
                  </svg>
                </Tooltip>
              </InputLabel>

              <div style={{
                border: '1px dashed #667185',
                borderRadius: '12px',
                padding: '24px',
                backgroundColor: '#fff',
                textAlign: 'center',
                position: 'relative'
              }}>
                <input
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  id="utility-bill-upload"
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                />
                <InputLabel
                  className="kyc_label"
                  htmlFor="utilityBill"
                  style={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#101828',
                    marginBottom: '8px',
                    display: 'block'
                  }}
                >
                  Upload the facility utility bill for the past 6-12months
                </InputLabel>

                <div style={{
                  fontSize: '12px',
                  color: '#667185',
                  fontWeight: 400,
                  lineHeight: '18px'

                }}>
                  <div>Image can be jpeg, jpg, or png</div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="utility-bill-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      style={{
                        border: '1px solid #D0D5DD',
                        borderRadius: '8px',
                        padding: '10px 16px',
                        color: '#344054',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
                        marginRight: '12px',
                        marginTop: '12px'
                      }}
                    >
                      Browse File
                    </Button>
                  </label>
                </div>
              </div>

              {/* FILE PREVIEW GALLERY */}
              <FilePreviewGallery
                files={facilityInfo.utilityBill}
                onRemove={handleRemoveUtilityBill}
              />
            </div>



            {/* Show upload error if any */}
            {uploadError && (
              <div style={{
                color: '#d32f2f',
                fontSize: '14px',
                padding: '8px 12px',
                backgroundColor: '#ffebee',
                borderRadius: '4px',
                marginTop: '8px'
              }}>
                {uploadError}
              </div>
            )}

            <button className="kyc_submit" type="submit">Complete</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KYCBusinessMonitoringForm;